export const lessonsData = [
  {
    id: 1,
    title: "Counting & Cardinality (Counting to 120)",
    vocabulary: [
      { term: "Counting", definition: "Saying numbers in order" },
      { term: "Number Order", definition: "Numbers arranged from smallest to largest" },
      { term: "Before/After", definition: "The number that comes right before or after another number" },
      { term: "Between", definition: "A number that comes after one number and before another" }
    ],
    conceptExplanation: `When we count, we say numbers in order: 1, 2, 3, 4, 5... and so on!

In first grade, we learn to count all the way to 120. That's a lot of numbers!

Here are some important ideas:
• Every number has a number that comes BEFORE it (the number is 1 less)
• Every number has a number that comes AFTER it (the number is 1 more)
• We can find numbers that come BETWEEN two other numbers

For example:
• The number BEFORE 10 is 9
• The number AFTER 10 is 11
• The number BETWEEN 10 and 12 is 11

When we count by 10s, we say: 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120!`,
    examples: [
      {
        problem: "What number comes right after 47?",
        steps: [
          "Step 1: We need to find the number that is 1 more than 47.",
          "Step 2: When we count: 45, 46, 47, 48...",
          "Step 3: The number after 47 is 48.",
          "Answer: 48"
        ]
      },
      {
        problem: "What number comes between 29 and 31?",
        steps: [
          "Step 1: We need to find the number that comes after 29 and before 31.",
          "Step 2: Let's count: 29, 30, 31",
          "Step 3: The number between 29 and 31 is 30.",
          "Answer: 30"
        ]
      },
      {
        problem: "Count by 10s from 50 to 90. What numbers do you say?",
        steps: [
          "Step 1: Start at 50.",
          "Step 2: Add 10 each time: 50, 60, 70, 80, 90",
          "Step 3: We say five numbers when counting by 10s from 50 to 90.",
          "Answer: 50, 60, 70, 80, 90"
        ]
      }
    ],
    practiceQuestions: [
      {
        question: "What number comes right before 56?",
        options: ["54", "55", "57", "58"],
        correctAnswer: 1,
        explanation: "When we count backwards from 56, we say 55. The number before 56 is 55."
      },
      {
        question: "Which number is between 78 and 80?",
        options: ["77", "79", "81", "82"],
        correctAnswer: 1,
        explanation: "When we count 78, 79, 80, we see that 79 comes between 78 and 80."
      },
      {
        question: "If you count by 10s starting at 30, what is the next number?",
        options: ["31", "35", "40", "50"],
        correctAnswer: 2,
        explanation: "When counting by 10s, we add 10 each time. 30 + 10 = 40."
      },
      {
        question: "What number comes after 99?",
        options: ["98", "100", "101", "110"],
        correctAnswer: 1,
        explanation: "After 99 comes 100. This is an important number because it's when we reach three digits!"
      }
    ],
    teacherTip: "Practice counting every day! Count toys, count steps, count snacks. The more you practice, the easier it gets. When counting past 100, remember the pattern: 101, 102, 103... it's just like counting to 20, but with 100 in front!"
  },
  {
    id: 2,
    title: "Addition within 20 (Counting On & Make-a-Ten)",
    vocabulary: [
      { term: "Addition", definition: "Putting two or more numbers together to find the total" },
      { term: "Sum", definition: "The answer when you add numbers together" },
      { term: "Counting On", definition: "Starting with one number and counting up to add" },
      { term: "Make-a-Ten", definition: "A strategy where we make 10 first, then add the rest" }
    ],
    conceptExplanation: `Addition means putting numbers together to find how many in all.

There are two great strategies for adding:

**Counting On Strategy:**
Start with the bigger number and count up!
Example: 8 + 3
Start at 8, then count: 9, 10, 11
Answer: 11

**Make-a-Ten Strategy:**
Make 10 first, then add what's left!
Example: 8 + 5
• 8 needs 2 more to make 10
• Break 5 into 2 and 3
• 8 + 2 = 10, then 10 + 3 = 13
Answer: 13

The Make-a-Ten strategy is super helpful because adding to 10 is easy!`,
    examples: [
      {
        problem: "Solve 7 + 4 using the Counting On strategy",
        steps: [
          "Step 1: Start with the bigger number, which is 7.",
          "Step 2: Count on 4 more: 8 (that's 1), 9 (that's 2), 10 (that's 3), 11 (that's 4)",
          "Step 3: We counted to 11.",
          "Answer: 7 + 4 = 11"
        ]
      },
      {
        problem: "Solve 9 + 6 using the Make-a-Ten strategy",
        steps: [
          "Step 1: 9 needs 1 more to make 10.",
          "Step 2: Break 6 into 1 and 5. So 9 + 1 = 10",
          "Step 3: Now add the leftover 5: 10 + 5 = 15",
          "Answer: 9 + 6 = 15"
        ]
      },
      {
        problem: "Emma has 8 crayons. Her friend gives her 7 more. How many crayons does Emma have now?",
        steps: [
          "Step 1: We need to add 8 + 7.",
          "Step 2: Use Make-a-Ten: 8 needs 2 to make 10. Break 7 into 2 and 5.",
          "Step 3: 8 + 2 = 10, then 10 + 5 = 15",
          "Answer: Emma has 15 crayons"
        ]
      }
    ],
    practiceQuestions: [
      {
        question: "What is 6 + 5?",
        options: ["10", "11", "12", "13"],
        correctAnswer: 1,
        explanation: "Using Make-a-Ten: 6 + 4 = 10, and we have 1 left from the 5. So 10 + 1 = 11."
      },
      {
        question: "If you count on from 9 to add 9 + 3, what is the sum?",
        options: ["11", "12", "13", "14"],
        correctAnswer: 1,
        explanation: "Start at 9 and count 3 more: 10, 11, 12. The answer is 12."
      },
      {
        question: "What is 7 + 8?",
        options: ["14", "15", "16", "17"],
        correctAnswer: 1,
        explanation: "Using Make-a-Ten: 7 + 3 = 10, and we have 5 left from the 8. So 10 + 5 = 15."
      },
      {
        question: "Tom has 5 toy cars. He gets 9 more for his birthday. How many toy cars does he have now?",
        options: ["13", "14", "15", "16"],
        correctAnswer: 1,
        explanation: "We add 5 + 9. Using Make-a-Ten: 5 + 5 = 10, then 10 + 4 = 14. Tom has 14 toy cars."
      }
    ],
    teacherTip: "The Make-a-Ten strategy is like a superpower for addition! Once you can quickly make 10, adding becomes so much easier. Practice your combinations that make 10: 1+9, 2+8, 3+7, 4+6, 5+5. Know these by heart!"
  },
  {
    id: 3,
    title: "Subtraction within 20 (Counting Back & Fact Families)",
    vocabulary: [
      { term: "Subtraction", definition: "Taking away or finding the difference between numbers" },
      { term: "Difference", definition: "The answer when you subtract" },
      { term: "Counting Back", definition: "Starting with a number and counting down to subtract" },
      { term: "Fact Family", definition: "A group of related addition and subtraction facts using the same numbers" }
    ],
    conceptExplanation: `Subtraction means taking away or finding how many are left.

**Counting Back Strategy:**
Start with the bigger number and count backwards!
Example: 11 - 3
Start at 11, count back 3: 10, 9, 8
Answer: 8

**Fact Families:**
Fact families show how addition and subtraction are related!
Example with numbers 5, 3, and 8:
• 5 + 3 = 8
• 3 + 5 = 8
• 8 - 3 = 5
• 8 - 5 = 3

All four facts use the same three numbers! If you know one fact, you can figure out the others.

Subtraction is the opposite of addition. If 7 + 5 = 12, then 12 - 5 = 7!`,
    examples: [
      {
        problem: "Solve 14 - 5 using the Counting Back strategy",
        steps: [
          "Step 1: Start at 14.",
          "Step 2: Count back 5: 13 (that's 1), 12 (that's 2), 11 (that's 3), 10 (that's 4), 9 (that's 5)",
          "Step 3: We counted back to 9.",
          "Answer: 14 - 5 = 9"
        ]
      },
      {
        problem: "Complete the fact family for 6, 7, and 13",
        steps: [
          "Step 1: Write the two addition facts: 6 + 7 = 13 and 7 + 6 = 13",
          "Step 2: Write the two subtraction facts: 13 - 7 = 6 and 13 - 6 = 7",
          "Step 3: All four facts use the numbers 6, 7, and 13.",
          "Answer: 6+7=13, 7+6=13, 13-7=6, 13-6=7"
        ]
      },
      {
        problem: "Maya had 15 stickers. She gave 8 to her sister. How many stickers does Maya have left?",
        steps: [
          "Step 1: We need to subtract: 15 - 8",
          "Step 2: Think: 8 + ? = 15. We know 8 + 7 = 15",
          "Step 3: So 15 - 8 = 7",
          "Answer: Maya has 7 stickers left"
        ]
      }
    ],
    practiceQuestions: [
      {
        question: "What is 12 - 4?",
        options: ["6", "7", "8", "9"],
        correctAnswer: 2,
        explanation: "Count back from 12: 11, 10, 9, 8. Or think: 4 + 8 = 12, so 12 - 4 = 8."
      },
      {
        question: "If 9 + 5 = 14, what is 14 - 5?",
        options: ["8", "9", "10", "11"],
        correctAnswer: 1,
        explanation: "These are in the same fact family! If 9 + 5 = 14, then 14 - 5 = 9."
      },
      {
        question: "What is 16 - 9?",
        options: ["5", "6", "7", "8"],
        correctAnswer: 2,
        explanation: "Think: 9 + 7 = 16, so 16 - 9 = 7. You can also count back 9 from 16."
      },
      {
        question: "There were 13 birds on a tree. 6 birds flew away. How many birds are still on the tree?",
        options: ["6", "7", "8", "9"],
        correctAnswer: 1,
        explanation: "We subtract: 13 - 6 = 7. There are 7 birds still on the tree."
      }
    ],
    teacherTip: "Fact families are your friends! When you learn an addition fact, you're actually learning four facts at once. Practice saying them out loud: 'If 8 plus 6 equals 14, then 14 minus 6 equals 8!' This helps your brain make connections."
  },
  {
    id: 4,
    title: "Place Value (Tens and Ones)",
    vocabulary: [
      { term: "Place Value", definition: "The value of a digit based on its position in a number" },
      { term: "Tens", definition: "Groups of 10 ones" },
      { term: "Ones", definition: "Single units" },
      { term: "Greater Than/Less Than", definition: "Comparing which number is bigger or smaller" }
    ],
    conceptExplanation: `Every digit in a number has a special place and value!

In a two-digit number like 47:
• The 4 is in the TENS place. It means 4 tens, or 40.
• The 7 is in the ONES place. It means 7 ones, or 7.
• Together: 40 + 7 = 47

**Understanding Tens:**
1 ten = 10 ones
2 tens = 20 ones
3 tens = 30 ones
...and so on!

**Comparing Numbers:**
To compare two numbers, look at the tens place first!
• 56 vs 48: 5 tens is more than 4 tens, so 56 > 48
• If the tens are the same, look at the ones place
• 34 vs 37: Both have 3 tens, but 7 ones is more than 4 ones, so 37 > 34

We use these symbols:
> means "greater than" (bigger)
< means "less than" (smaller)
= means "equal to" (the same)`,
    examples: [
      {
        problem: "What is the value of the digit 6 in the number 63?",
        steps: [
          "Step 1: Look at where the 6 is. It's in the tens place.",
          "Step 2: The 6 in the tens place means 6 tens.",
          "Step 3: 6 tens = 60",
          "Answer: The digit 6 has a value of 60"
        ]
      },
      {
        problem: "Compare 52 and 58. Which is greater?",
        steps: [
          "Step 1: Look at the tens place first. Both have 5 tens.",
          "Step 2: Since the tens are the same, look at the ones place.",
          "Step 3: 8 ones is more than 2 ones, so 58 is greater.",
          "Answer: 58 > 52"
        ]
      },
      {
        problem: "Show 45 as tens and ones",
        steps: [
          "Step 1: The 4 is in the tens place, so we have 4 tens.",
          "Step 2: The 5 is in the ones place, so we have 5 ones.",
          "Step 3: We can write this as: 4 tens + 5 ones = 45",
          "Answer: 45 = 4 tens and 5 ones"
        ]
      }
    ],
    practiceQuestions: [
      {
        question: "How many tens are in the number 72?",
        options: ["2", "7", "10", "72"],
        correctAnswer: 1,
        explanation: "The digit 7 is in the tens place, so there are 7 tens in 72."
      },
      {
        question: "Which number is less than 64?",
        options: ["68", "64", "59", "70"],
        correctAnswer: 2,
        explanation: "59 is less than 64 because 5 tens is less than 6 tens. We write: 59 < 64."
      },
      {
        question: "What number is the same as 3 tens and 8 ones?",
        options: ["83", "38", "11", "308"],
        correctAnswer: 1,
        explanation: "3 tens = 30, and 8 ones = 8. Together: 30 + 8 = 38."
      },
      {
        question: "Which symbol makes this true? 45 ___ 54",
        options: [">", "<", "=", "+"],
        correctAnswer: 1,
        explanation: "45 has 4 tens and 54 has 5 tens. Since 4 tens is less than 5 tens, we write: 45 < 54."
      }
    ],
    teacherTip: "Use base-ten blocks or draw pictures to see tens and ones! Draw a long stick for each ten and a dot for each one. This helps you really understand that 47 is 4 long sticks (tens) and 7 dots (ones). The more you visualize it, the easier place value becomes!"
  },
  {
    id: 5,
    title: "Measurement & Time",
    vocabulary: [
      { term: "Length", definition: "How long something is from one end to the other" },
      { term: "Measure", definition: "To find out how long, tall, or wide something is" },
      { term: "Hour", definition: "60 minutes; the short hand on a clock shows hours" },
      { term: "Half Hour", definition: "30 minutes; halfway between two hours" }
    ],
    conceptExplanation: `**Measuring Length:**
We can measure how long things are using different units!
• We can use paper clips, blocks, or other objects to measure
• Line up the objects end-to-end with no gaps
• Count how many objects fit along the length

Example: A pencil might be 5 paper clips long!

**Telling Time:**
A clock has two hands:
• The SHORT hand points to the HOUR
• The LONG hand points to the MINUTES

**Hour (o'clock):**
When the long hand points to 12, it's exactly on the hour!
• If the short hand points to 3 and long hand points to 12, it's 3:00 (3 o'clock)

**Half Hour:**
When the long hand points to 6, it's half past the hour (30 minutes after)
• If the short hand is between 3 and 4, and the long hand points to 6, it's 3:30 (half past 3)

Remember: The short hand moves slowly and shows the hour. The long hand moves faster and shows the minutes!`,
    examples: [
      {
        problem: "A crayon is 4 paper clips long. A marker is 6 paper clips long. How much longer is the marker?",
        steps: [
          "Step 1: The marker is 6 paper clips long.",
          "Step 2: The crayon is 4 paper clips long.",
          "Step 3: Subtract to find the difference: 6 - 4 = 2",
          "Answer: The marker is 2 paper clips longer"
        ]
      },
      {
        problem: "What time is shown when the short hand points to 7 and the long hand points to 12?",
        steps: [
          "Step 1: The long hand is on 12, so it's exactly on the hour.",
          "Step 2: The short hand points to 7.",
          "Step 3: This means it's 7 o'clock.",
          "Answer: 7:00 (seven o'clock)"
        ]
      },
      {
        problem: "The short hand is between 2 and 3, and the long hand points to 6. What time is it?",
        steps: [
          "Step 1: The long hand is on 6, so it's a half hour (30 minutes).",
          "Step 2: The short hand is between 2 and 3, so it's past 2 o'clock.",
          "Step 3: This means it's half past 2.",
          "Answer: 2:30 (two-thirty)"
        ]
      }
    ],
    practiceQuestions: [
      {
        question: "A book is 8 blocks long. A notebook is 5 blocks long. How much longer is the book?",
        options: ["2 blocks", "3 blocks", "4 blocks", "13 blocks"],
        correctAnswer: 1,
        explanation: "Subtract to find the difference: 8 - 5 = 3. The book is 3 blocks longer."
      },
      {
        question: "What time is it when the short hand points to 5 and the long hand points to 12?",
        options: ["12:05", "5:00", "5:30", "12:00"],
        correctAnswer: 1,
        explanation: "When the long hand is on 12, it's exactly on the hour. The short hand on 5 means 5:00."
      },
      {
        question: "If the long hand points to 6, how many minutes past the hour is it?",
        options: ["6 minutes", "15 minutes", "30 minutes", "60 minutes"],
        correctAnswer: 2,
        explanation: "When the long hand points to 6, it's 30 minutes past the hour (half past)."
      },
      {
        question: "School starts at 8:00. Lunch is at 12:00. How many hours is that?",
        options: ["2 hours", "4 hours", "6 hours", "8 hours"],
        correctAnswer: 1,
        explanation: "Count from 8 to 12: 8, 9, 10, 11, 12. That's 4 hours."
      }
    ],
    teacherTip: "Practice telling time throughout the day! Every time you look at a clock, say the time out loud. For measuring, use fun objects like toy cars, crayons, or your own hand spans. The more you practice with real objects, the better you'll understand measurement!"
  },
  {
    id: 6,
    title: "Geometry & Fractions (Shapes & Equal Parts)",
    vocabulary: [
      { term: "2D Shape", definition: "A flat shape like a circle, square, or triangle" },
      { term: "3D Shape", definition: "A solid shape like a cube, sphere, or cone" },
      { term: "Half", definition: "One of two equal parts" },
      { term: "Fourth (Quarter)", definition: "One of four equal parts" }
    ],
    conceptExplanation: `**2D Shapes (Flat Shapes):**
• Circle: Round, no corners
• Square: 4 equal sides, 4 corners
• Rectangle: 4 sides (opposite sides equal), 4 corners
• Triangle: 3 sides, 3 corners

**3D Shapes (Solid Shapes):**
• Sphere: Like a ball, round all around
• Cube: Like a box, 6 square faces
• Cylinder: Like a can, 2 circular ends
• Cone: Like an ice cream cone, 1 circular base and 1 point

**Equal Parts (Fractions):**
When we divide a shape into equal parts, each part is the same size!

**Halves:**
• 2 equal parts
• Each part is called one half (1/2)
• Example: Cut a pizza into 2 equal pieces

**Fourths (Quarters):**
• 4 equal parts
• Each part is called one fourth (1/4) or one quarter
• Example: Cut a sandwich into 4 equal pieces

Important: The parts must be EQUAL (the same size) to be halves or fourths!`,
    examples: [
      {
        problem: "Name this shape: It has 4 equal sides and 4 corners.",
        steps: [
          "Step 1: The shape has 4 sides, so it could be a square or rectangle.",
          "Step 2: All 4 sides are EQUAL, which means it's a square.",
          "Step 3: Rectangles have 4 sides too, but only opposite sides are equal.",
          "Answer: Square"
        ]
      },
      {
        problem: "A circle is divided into 2 equal parts. What fraction is each part?",
        steps: [
          "Step 1: The circle is divided into 2 equal parts.",
          "Step 2: When something is divided into 2 equal parts, each part is called a half.",
          "Step 3: We write one half as 1/2.",
          "Answer: Each part is 1/2 (one half)"
        ]
      },
      {
        problem: "A rectangle is divided into 4 equal parts. Sarah colors 1 part blue. What fraction did she color?",
        steps: [
          "Step 1: The rectangle has 4 equal parts total.",
          "Step 2: Sarah colored 1 out of the 4 parts.",
          "Step 3: This is one fourth, written as 1/4.",
          "Answer: Sarah colored 1/4 of the rectangle"
        ]
      }
    ],
    practiceQuestions: [
      {
        question: "Which shape is like a ball?",
        options: ["Cube", "Sphere", "Cylinder", "Cone"],
        correctAnswer: 1,
        explanation: "A sphere is round like a ball. It's a 3D shape with no flat faces."
      },
      {
        question: "A square is divided into 2 equal parts. What is each part called?",
        options: ["One whole", "One half", "One fourth", "One third"],
        correctAnswer: 1,
        explanation: "When a shape is divided into 2 equal parts, each part is one half (1/2)."
      },
      {
        question: "How many equal parts are in one whole divided into fourths?",
        options: ["2", "3", "4", "5"],
        correctAnswer: 2,
        explanation: "Fourths means 4 equal parts. One whole divided into fourths has 4 parts."
      },
      {
        question: "A pizza is cut into 4 equal slices. You eat 1 slice. What fraction of the pizza did you eat?",
        options: ["1/2", "1/3", "1/4", "2/4"],
        correctAnswer: 2,
        explanation: "You ate 1 out of 4 equal slices. That's 1/4 (one fourth) of the pizza."
      }
    ],
    teacherTip: "Look for shapes everywhere! Find circles (plates, clocks), rectangles (doors, books), and 3D shapes (balls, boxes, cans) around your house. For fractions, practice with real food like sandwiches or pizza. When you cut something into equal parts and eat one piece, you're learning fractions in a delicious way!"
  }
];